import { Base } from "./Base";
import { throwExpected, ForbiddenException, UnprocessableEntityException, BadRequestException, NotFoundException, ConflictException, UnauthorizedException, MultiStatusException, PaymentRequiredException, } from "./exceptions";
/**
 * ^: This anchor matches the start of the string.
 * [A-Za-z0-9]: This matches any English letter or digit.
 * [A-Za-z0-9-]{2,14}: This character set matches any English letter,
 * digit or the - character, and repeats between 3 and 15 times.
 * [A-Za-z0-9]: This matches any English letter or digit.
 * $: This anchor matches the end of the string.
 */
export const businessHandleValidation = /^[A-Za-z0-9][A-Za-z0-9-]{2,14}[A-Za-z0-9]$/;
export var SSOType;
(function (SSOType) {
    SSOType["SAML"] = "saml";
    SSOType["OIDC"] = "oidc";
})(SSOType || (SSOType = {}));
/**
 * The keys that are allowed to be updated/replaced.
 */
export const allowedUpdateBusinessKeys = [
    "handle",
    "name",
    "address",
    "additionalAddress",
    "country",
    "city",
    "state",
    "zip",
    "phoneNumber",
    "verifiedDomains",
    "websiteUrl",
    "department",
    "ssoAutoJoin",
    "automaticSeatAssignment",
    "autoAcceptJoinRequests",
    "emailDomainMatchingEnabled",
    "emailDomainWelcomeMessage",
];
function validateUpdateBusinessKeys(businessObject, allowedUpdateBusinessKeys) {
    const validatedObject = Object.entries(businessObject).reduce((acc, [key, value]) => {
        if (allowedUpdateBusinessKeys.includes(key)) {
            acc[key] = value;
        }
        if (key === "verifiedDomains" && Array.isArray(value)) {
            const domains = value;
            acc[key] = domains.map(({ domain }) => ({
                domain,
            }));
        }
        return acc;
    }, {});
    return validatedObject;
}
class BusinessService extends Base {
    /**
     * Lists business entities ("Lens Business ID") that the authenticated user has explicit permissions to access.
     */
    async getMany() {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses`;
        const json = await throwExpected(async () => fetch.get(url), {
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Get one business entity ("Lens Business ID") by id.
     */
    async getOne(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Create a new business ("Lens Business ID").
     */
    async createOne(business) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses`;
        const json = await throwExpected(async () => fetch.post(url, business), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
            409: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Update an existing business ("Lens Business ID").
     */
    async updateOne(id, business) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}`;
        const json = await throwExpected(async () => fetch.patch(url, validateUpdateBusinessKeys(business, allowedUpdateBusinessKeys)), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
            401: (error) => new UnauthorizedException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
            409: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Replace an existing business ("Lens Business ID").
     */
    async replaceOne(id, business) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}`;
        const json = await throwExpected(async () => fetch.put(url, validateUpdateBusinessKeys(business, allowedUpdateBusinessKeys)), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            401: (error) => new UnauthorizedException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
            409: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Disable business light activation link
     */
    async disableBusinessLightActivationLink(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/activation-link`;
        await throwExpected(async () => fetch.delete(url), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
            401: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
    }
    /**
     * Delete business entity ("Lens Business ID") by id.
     */
    async deleteOne(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}`;
        await throwExpected(async () => fetch.delete(url), {
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
    }
    /**
     * Delete user from the business.
     */
    async deleteBusinessUser(businessId, businessUserId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/users/${businessUserId}`;
        await throwExpected(async () => fetch.delete(url), {
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
        });
    }
    /**
     * Change existing business user role
     */
    async changeBusinessUserRole(businessId, businessUserId, role) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/users/${businessUserId}`;
        const json = await throwExpected(async () => fetch.patch(url, { role }), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            401: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Lists the subscriptions by id
     */
    async getSubscriptions(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/subscriptions`;
        const json = await throwExpected(async () => fetch.get(url), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Create a new subscription by planCode
     *
     * @remarks only use by LBID that has a parent LBID and the parent has valid billing info.
     */
    async createSubscription(id, planCode, quantity) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/subscriptions`;
        const json = await throwExpected(async () => fetch.post(url, { planCode, quantity }), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Activate user business subscription seat
     */
    async activateBusinessUserSubscription({ businessId, businessSubscriptionId, businessInvitationId, userId, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/subscription-seats`;
        const json = await throwExpected(async () => fetch.post(url, {
            invitationId: businessInvitationId,
            subscriptionId: businessSubscriptionId,
            userId,
        }), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Deactivate user business subscription seat
     * Request user has to be an owner of the subscription seat or Business Administrator
     */
    async deActivateBusinessUserSubscription({ businessId, businessSubscriptionId, username, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/subscription-seats/${businessSubscriptionId}`;
        const json = await throwExpected(async () => fetch.patch(url), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Modification of user licenses for ${username} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Change business subscription seat quantity
     */
    async changeBusinessSubscriptionSeatsQuantity({ businessId, businessSubscriptionId, quantity, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/subscriptions/${businessSubscriptionId}`;
        const json = await throwExpected(async () => fetch.patch(url, { quantity }), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Modification of subscription for business ${businessId} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Preview business subscription seat quantity change
     */
    async previewBusinessSubscriptionSeatsQuantityChange({ businessId, businessSubscriptionId, quantity, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/subscriptions/${businessSubscriptionId}/change/preview`;
        const json = await throwExpected(async () => fetch.post(url, { quantity }), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Modification of subscription for business ${businessId} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Change business subscription custom fields
     */
    async updateBusinessSubscriptionCustomField({ businessId, businessSubscriptionId, customField, }) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/subscriptions/${businessSubscriptionId}/custom-field`;
        const json = await throwExpected(async () => fetch.patch(url, customField), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Modification of subscription for business ${businessId} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Lists the users in the business by id
     */
    async getUsers(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/users`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Get the list of business invitations by business id
     */
    async getInvitations(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/invitations`;
        const json = await throwExpected(async () => fetch.get(url), {
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Get one business invitation by an invitation id
     */
    async getOneInvitation(businessId, invitationId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/invitations/${invitationId}`;
        const json = await throwExpected(async () => fetch.get(url), {
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Create a new invitation for a user to join a business, optionally assigning them to a
     * subscription by given subscriptionId.
     *
     * @remarks inviter has to be the administrator of the business
     */
    async createInvitation(id, email, subscriptionId, role = "Member") {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/invitations`;
        const json = await throwExpected(async () => fetch.post(url, { email, subscriptionId, role }), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            409: (error) => new ConflictException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Accept an invitation to join a business.
     */
    async acceptInvitation(id, invitationId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/invitations/${invitationId}`;
        const json = await throwExpected(async () => fetch.patch(url, {
            state: "active",
        }), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Delete business invitation
     */
    async deleteInvitation(id, invitationId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/invitations/${invitationId}`;
        const json = await throwExpected(async () => fetch.delete(url), {
            400: (error) => new BadRequestException(error === null || error === void 0 ? void 0 : error.body.message),
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Get token for Recurly hosted pages
     *
     * @remarks user has to be the administrator of the business
     */
    async getBillingPageToken(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/billing-page-token`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: () => new ForbiddenException(`Getting the billing page token for businessId ${id} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Create token for Recurly hosted pages
     *
     * @remarks user has to be the administrator of the business
     */
    async createBillingPageToken(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/billing-page-token`;
        const json = await throwExpected(async () => fetch.post(url), {
            404: (error) => new NotFoundException(error === null || error === void 0 ? void 0 : error.body.message),
            403: (error) => new ForbiddenException(error === null || error === void 0 ? void 0 : error.body.message),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * Get business billing information
     *
     */
    async getBusinessBillingInformation(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/billing`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new NotFoundException(`Business ${id} not found`),
            403: () => new ForbiddenException(`Getting the billing information for business ${id} is forbidden`),
        });
        return json;
    }
    /**
     * Update business billing information
     *
     */
    async updateBusinessBillingInformation(id, billingInfo) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/billing`;
        const json = await throwExpected(async () => fetch.put(url, billingInfo), {
            402: (error) => { var _a; return new PaymentRequiredException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message, error); },
            404: () => new NotFoundException(`Business ${id} not found`),
            403: () => new ForbiddenException(`Updating the billing information for business ${id} is forbidden`),
            422: (error) => new UnprocessableEntityException(error === null || error === void 0 ? void 0 : error.body.message),
        });
        return json;
    }
    /**
     * List all children LBIDs of a LBID.
     *
     * @remarks user has to be the administrator of the business.
     */
    async getChildren(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/businesses`;
        const json = await throwExpected(async () => fetch.get(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Remove all children LBIDs of a LBID.
     *
     * @remarks user has to be the administrator of the business.
     */
    async removeAllChildren(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/businesses`;
        await throwExpected(async () => fetch.delete(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    /**
     * Remove one child LBID of a LBID.
     *
     * @remarks user has to be the administrator of the business.
     * @returns the remaining children LBIDs.
     */
    async removeOneChild(parentId, childId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${parentId}/businesses/${childId}`;
        const json = await throwExpected(async () => fetch.delete(url), {
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Get the parent LBID of a LBID.
     *
     * @remarks One LBID can only have one parent.
     * @remarks user has to be the administrator of the business.
     */
    async getParent(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/parent`;
        const json = await throwExpected(async () => fetch.get(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Remove the parent LBID of a LBID.
     *
     * @remarks user has to be the administrator of the business.
     */
    async removeParent(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/parent`;
        await throwExpected(async () => fetch.delete(url), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    /**
     * Create a new 'hierarchy' invitation for a LBID to join another LBID as child account.
     *
     * @remarks should be used by parent LBID admins.
     */
    async createChildInvitation(parentId, expiryTime) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${parentId}/hierarchies/invitations`;
        const json = await throwExpected(async () => fetch.post(url, expiryTime ? { expiryTime } : {}), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            409: (error) => { var _a; return new ConflictException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * List all 'hierarchy' invitations by LBID id.
     *
     * @remarks should be used by parent LBID admins.
     */
    async getManyChildrenInvitation(parentId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${parentId}/hierarchies/invitations`;
        const json = await throwExpected(async () => fetch.get(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * List all 'hierarchy' invitations by LBID id.
     *
     * @remarks should be used by child LBID admins.
     */
    async getOneChildrenInvitationByToken(token) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/hierarchies/invitations?token=${token}`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Update 'hierarchy' invitations by the invitation id and the LBID id.
     *
     * @remarks should be used by parent LBID admins.
     */
    async updateOneChildInvitation(parentId, invitationId, state) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${parentId}/hierarchies/invitations/${invitationId}`;
        const json = await throwExpected(async () => fetch.patch(url, { state }), {
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Accept or reject or confirm the 'hierarchy' invitation by `token`.
     *
     * @remarks should be used by child LBID admins.
     */
    async acceptChildInvitation(childId, state, token) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${childId}/hierarchies/invitations`;
        const json = await throwExpected(async () => fetch.patch(url, { state, token }), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            409: (error) => { var _a; return new ConflictException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * List all invoices for a business
     *
     */
    async getBusinessInvoices(businessId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/invoices`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new NotFoundException(`Business ${businessId} not found`),
            403: () => new ForbiddenException(`Getting the invoices for ${businessId} is forbidden`),
        });
        return json;
    }
    /**
     * Get business SSO details
     *
     */
    async getBusinessSSO(businessId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessId}/sso`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: () => new NotFoundException(`Business ${businessId} not found`),
            403: () => new ForbiddenException(`Getting the SSO for ${businessId} is forbidden`),
        });
        return json;
    }
    /**
     * Create business SSO
     *
     */
    async createBusinessSSO(businessID, ssoSettings) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/sso`;
        const json = await throwExpected(async () => fetch.post(url, ssoSettings), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            409: (error) => { var _a; return new ConflictException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Delete business SSO
     *
     */
    async removeBusinessSSO(id) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${id}/sso`;
        await throwExpected(async () => fetch.delete(url), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    /**
     * Send business SSO activation emails
     *
     */
    async sendBusinessSSOActivationEmail(businessID) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/sso/activation-email`;
        await throwExpected(async () => fetch.post(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    /**
     * Get list of Lens Business ID features
     */
    async getBusinessFeatures(businessID) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/features`;
        const json = await throwExpected(async () => fetch.get(url), {
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Update Lens Business ID features
     */
    async updateBusinessFeatures(businessID, features) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/features`;
        const json = await throwExpected(async () => fetch.put(url, features), {
            401: (error) => { var _a; return new UnauthorizedException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Get list of Lens Business ID join requests.
     *
     * @remarks should be used by LBID admins.
     */
    async getBusinessJoinRequests(businessID) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/join-requests`;
        const json = await throwExpected(async () => fetch.get(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Accept/reject/cancel a Lens Business ID join request.
     *
     * @remarks should be used by LBID admins for accept/reject, and by users for cancel.
     */
    async updateBusinessJoinRequest(businessID, joinRequestId, state) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/join-requests/${joinRequestId}`;
        const json = await throwExpected(async () => fetch.patch(url, { state }), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Accept/reject/cancel multiple Lens Business ID join request.
     *
     * @remarks should be used by LBID admins for accept/reject, and by users for cancel.
     */
    async updateBusinessJoinRequests(businessID, data) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/join-requests`;
        const json = await throwExpected(async () => fetch.patch(url, data), {
            207: (error) => {
                if ((error === null || error === void 0 ? void 0 : error.body) && (error === null || error === void 0 ? void 0 : error.body.error)) {
                    const response = error.body;
                    let message = response.error;
                    // Better error message for multi-status
                    if (response["multi-status"]) {
                        const multiStatus = response["multi-status"];
                        const failed = multiStatus.filter((e) => e.status !== "success");
                        message = `Failed to update ${failed.length} out of ${multiStatus.length} join requests. Please try again later.`;
                    }
                    return new MultiStatusException(message, error);
                }
                return new MultiStatusException(undefined, error);
            },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Create a Lens Business ID join request.
     *
     * @remarks should be used to create the request to join a Lens Business ID.
     */
    async createBusinessJoinRequest(businessID) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/join-requests`;
        const json = await throwExpected(async () => fetch.post(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            409: (error) => { var _a; return new ConflictException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            422: (error) => { var _a; return new UnprocessableEntityException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Get a list of Lens Business ID SCIM tokens.
     *
     * @remarks should be used by LBID admins.
     */
    async getBusinessSCIMTokens(businessID) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/scimtokens`;
        const json = await throwExpected(async () => fetch.get(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
    /**
     * Delete a Lens Business ID SCIM token.
     *
     * @remarks should be used by LBID admins.
     */
    async deleteBusinessSCIMToken(businessID, tokenId) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/scimtokens/${tokenId}`;
        await throwExpected(async () => fetch.delete(url), {
            400: (error) => { var _a; return new BadRequestException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
            404: (error) => { var _a; return new NotFoundException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
    }
    /**
     * Create a Lens Business ID SCIM token.
     *
     * @remarks should be used by LBID admins.
     */
    async createBusinessSCIMToken(businessID) {
        const { apiEndpointAddress, fetch } = this.lensPlatformClient;
        const url = `${apiEndpointAddress}/businesses/${businessID}/scimtokens`;
        const json = await throwExpected(async () => fetch.post(url), {
            403: (error) => { var _a; return new ForbiddenException((_a = error === null || error === void 0 ? void 0 : error.body) === null || _a === void 0 ? void 0 : _a.message); },
        });
        return json;
    }
}
export { BusinessService };
